import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CreateGameInput, Game, UpdateGameInput } from './games-types';

// Separate schemas so create enforces package_name as required (matches
// CreateGameDto on BE), while update only validates name. The form's value
// type is CreateFormData (the superset) so the field names stay stable across
// create/edit; the update flow simply never sends `package_name` to the API.
const createSchema = z.object({
  package_name: z
    .string()
    .min(3, 'Must be at least 3 characters')
    .max(255, 'Must be at most 255 characters')
    .regex(/^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/, 'Must be like com.example.app'),
  name: z.string().min(1, 'Required').max(255, 'Must be at most 255 characters'),
});
const updateSchema = z.object({
  package_name: z.string().min(1),
  name: z.string().min(1, 'Required').max(255, 'Must be at most 255 characters'),
});
type CreateFormData = z.infer<typeof createSchema>;

interface GameFormProps {
  initial?: Game;
  onSubmit: (data: CreateGameInput | UpdateGameInput) => Promise<void> | void;
  onCancel: () => void;
  isSubmitting: boolean;
}

export function GameForm({ initial, onSubmit, onCancel, isSubmitting }: GameFormProps): JSX.Element {
  const isEdit = !!initial;
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateFormData>({
    resolver: zodResolver(isEdit ? updateSchema : createSchema),
    defaultValues: initial
      ? { package_name: initial.package_name, name: initial.name }
      : { package_name: '', name: '' },
  });

  useEffect(() => {
    if (initial) reset({ package_name: initial.package_name, name: initial.name });
    else reset({ package_name: '', name: '' });
  }, [initial, reset]);

  function handleFormSubmit(data: CreateFormData): void {
    if (isEdit) {
      void onSubmit({ name: data.name } as UpdateGameInput);
    } else {
      void onSubmit({ package_name: data.package_name, name: data.name } as CreateGameInput);
    }
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      {!isEdit && (
        <div className="space-y-2">
          <Label htmlFor="package_name">Package name</Label>
          <Input id="package_name" placeholder="com.example.app" {...register('package_name')} />
          {errors.package_name && (
            <p className="text-sm text-destructive">{errors.package_name.message as string}</p>
          )}
          <p className="text-xs text-muted-foreground">
            e.g. <code>com.example.game1</code>. Locked after creation.
          </p>
        </div>
      )}
      <div className="space-y-2">
        <Label htmlFor="name">Display name</Label>
        <Input id="name" placeholder="My Game" {...register('name')} />
        {errors.name && (
          <p className="text-sm text-destructive">{errors.name.message as string}</p>
        )}
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving…' : isEdit ? 'Save' : 'Create'}
        </Button>
      </div>
    </form>
  );
}
